import React, { useState, useEffect } from "react";
import {
  Container,
  Tabs,
  Table,
  Button,
  NumberInput,
  Group,
  Text,
  Card,
  Alert,
  Title,
} from "@mantine/core";
import {
  IconCash,
  IconSettings,
  IconInfoCircle,
  IconCurrencyEuro,
  IconPackage,
  IconParking,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  getStandPricing,
  updateStandPricing,
  getPricingAnalytics,
} from "../misc/api";
import GenericCRUD from "./GenericCRUD";

// API objects for GenericCRUD
import { pprPricing, creditPackages } from "../misc/client";

const PPRPricingCRUD = () => (
  <GenericCRUD
    title="PPR Pricing"
    canCreate={true}
    api={pprPricing}
    columns={[
      { key: "pricing_code", label: "Code" },
      { key: "pricing_name", label: "Name" },
      { key: "flight_type", label: "Flight Type" },
      { key: "ppr_type", label: "PPR Type" },
      { key: "price", label: "Price (€)" },
      { key: "is_active", label: "Status" },
    ]}
    formFields={[
      {
        key: "pricing_code",
        label: "Pricing Code",
        placeholder: "PPR_SINGLE_DAY",
        required: true,
        disabled: "edit", // Disable when editing
      },
      {
        key: "pricing_name",
        label: "Pricing Name",
        placeholder: "Single Flying Day",
        required: true,
      },
      {
        key: "flight_type",
        label: "Flight Type",
        type: "select",
        options: [
          { value: "single_day", label: "Single Day" },
          { value: "single_arrival", label: "Single Arrival" },
          { value: "single_departure", label: "Single Departure" },
          { value: "alternate", label: "Alternate" },
        ],
        defaultValue: "single_day",
        required: true,
      },
      {
        key: "ppr_type",
        label: "PPR Type",
        type: "select",
        options: [
          { value: "one_time", label: "One-Time" },
          { value: "permanent", label: "Permanent" },
        ],
        defaultValue: "one_time",
        required: true,
      },
      {
        key: "price",
        label: "Price (EUR)",
        type: "number",
        precision: 2,
        step: 0.5,
        min: 0,
        defaultValue: 0,
        required: true,
      },
      {
        key: "currency",
        label: "Currency",
        type: "select",
        options: [
          { value: "EUR", label: "EUR" },
          { value: "USD", label: "USD" },
        ],
        defaultValue: "EUR",
      },
      // {
      //   key: "requires_payment",
      //   label: "Requires Payment",
      //   type: "switch",
      //   defaultValue: true,
      // },
      {
        key: "description",
        label: "Description",
        type: "textarea",
        rows: 3,
      },
    ]}
    formatters={{
      flight_type: (value) => value?.replace("_", " "),
      price: (value) => `€${value}`,
      is_active: (value) => (value ? "Active" : "Inactive"),
    }}
    customActions={[
      {
        label: "Deactivate",
        icon: "trash",
        action: async (item, api) => {
          if (
            window.confirm("Are you sure you want to deactivate this pricing?")
          ) {
            await api.update(item.pricing_id, { ...item, is_active: false });
            notifications.show({
              title: "Success",
              message: "PPR pricing deactivated",
              color: "green",
            });
          }
        },
        condition: (item) => item.is_active,
        color: "red",
      },
    ]}
    modalSize="md"
  />
);

const CreditPackagesCRUD = () => (
  <GenericCRUD
    title="Credit Package"
    canCreate={true}
    api={creditPackages}
    columns={[
      { key: "package_code", label: "Code" },
      { key: "package_name", label: "Name" },
      { key: "duration_months", label: "Duration" },
      { key: "credit_amount", label: "Credits" },
      { key: "price", label: "Price (€)" },
      { key: "price_per_credit", label: "Price/Credit" },
      { key: "is_active", label: "Status" },
    ]}
    formFields={[
      {
        key: "package_code",
        label: "Package Code",
        placeholder: "subscription_6m",
        required: true,
        disabled: "edit", // Disable when editing
      },
      {
        key: "package_name",
        label: "Package Name",
        placeholder: "6 Month Subscription",
        required: true,
      },
      {
        key: "duration_months",
        label: "Duration (Months)",
        type: "number",
        min: 1,
        max: 60,
        defaultValue: 1,
        required: true,
      },
      {
        key: "credit_amount",
        label: "Credit Amount",
        type: "number",
        min: 1,
        max: 1000,
        defaultValue: 5,
        required: true,
      },
      {
        key: "price",
        label: "Price (EUR)",
        type: "number",
        precision: 2,
        step: 5,
        min: 0,
        defaultValue: 50,
        required: true,
      },
    ]}
    formatters={{
      duration_months: (value) => `${value} month${value > 1 ? "s" : ""}`,
      price: (value) => `€${value}`,
      price_per_credit: (value) => `€${value}`,
      is_active: (value) => (value ? "Active" : "Inactive"),
    }}
    // Custom validation to show price per credit calculation
    validate={(data) => {
      const errors = {};

      if (!data.package_code?.trim()) {
        errors.package_code = "Package code is required";
      }

      if (!data.package_name?.trim()) {
        errors.package_name = "Package name is required";
      }

      if (data.credit_amount <= 0) {
        errors.credit_amount = "Credit amount must be greater than 0";
      }

      if (data.price <= 0) {
        errors.price = "Price must be greater than 0";
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }}
    // Show calculated price per credit in form
    formFooter={(data) => {
      if (data.credit_amount > 0 && data.price > 0) {
        const pricePerCredit = (data.price / data.credit_amount).toFixed(2);
        return (
          <Alert icon={<IconInfoCircle />} color="blue">
            Price per credit: €{pricePerCredit}
          </Alert>
        );
      }
      return null;
    }}
    modalSize="md"
  />
);

export const AdminPaymentSystem = () => {
  const [activeTab, setActiveTab] = useState("ppr-pricing");
  const [loading, setLoading] = useState(false);

  const [standPricing, setStandPricing] = useState([]);
  const [standPricingEdits, setStandPricingEdits] = useState({});

  useEffect(() => {
    loadStandPricing();
  }, []);

  const loadStandPricing = async () => {
    try {
      const response = await getStandPricing();
      setStandPricing(response.data || []);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to load stand pricing",
        color: "red",
      });
    }
  };

  const handleStandPriceChange = (standId, field, value) => {
    setStandPricingEdits((prev) => ({
      ...prev,
      [standId]: {
        ...prev[standId],
        stand_id: standId,
        [field]: value,
      },
    }));
  };

  const handleSaveStandPricing = async () => {
    const updates = Object.values(standPricingEdits);
    if (updates.length === 0) {
      notifications.show({
        title: "Info",
        message: "No changes to save",
        color: "blue",
      });
      return;
    }

    setLoading(true);
    try {
      await updateStandPricing(updates);

      notifications.show({
        title: "Success",
        message: "Stand pricing updated successfully",
        color: "green",
      });

      setStandPricingEdits({});
      loadStandPricing();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to update stand pricing",
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const getPriceWithEdit = (stand, field) => {
    return standPricingEdits[stand.stand_id]?.[field] ?? stand[field];
  };

  return (
    <Container size="xl" py="md">
      <Group position="apart" mb="lg">
        <Title order={2}>
          <IconCash size={28} style={{ marginRight: 8 }} />
          Payment & Pricing Management
        </Title>
      </Group>

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="ppr-pricing" icon={<IconCurrencyEuro size={14} />}>
            PPR Pricing
          </Tabs.Tab>
          <Tabs.Tab value="credit-packages" icon={<IconPackage size={14} />}>
            Credit Packages
          </Tabs.Tab>
          <Tabs.Tab value="stand-pricing" icon={<IconParking size={14} />}>
            Stand Pricing
          </Tabs.Tab>
        </Tabs.List>

        {/* PPR PRICING TAB - Now using GenericCRUD */}
        <Tabs.Panel value="ppr-pricing" pt="md">
          <PPRPricingCRUD />
        </Tabs.Panel>

        {/* CREDIT PACKAGES TAB - Now using GenericCRUD */}
        <Tabs.Panel value="credit-packages" pt="md">
          <CreditPackagesCRUD />
        </Tabs.Panel>

        {/* STAND PRICING TAB - Keeping original implementation */}
        <Tabs.Panel value="stand-pricing" pt="md">
          <Card withBorder>
            <Group position="apart" mb="md">
              <Text weight={600}>Aircraft Stand Parking Rates</Text>
              <Button
                leftIcon={<IconSettings size={16} />}
                onClick={handleSaveStandPricing}
                disabled={Object.keys(standPricingEdits).length === 0}
                loading={loading}
              >
                Save Changes
              </Button>
            </Group>

            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Stand</th>
                  <th>Type</th>
                  <th colSpan={3}>Light Aircraft Rates (€)</th>
                  <th colSpan={3}>Heavy Aircraft Rates (€)</th>
                </tr>
                <tr>
                  <th></th>
                  <th></th>
                  <th>Hourly</th>
                  <th>Daily</th>
                  <th>Monthly</th>
                  <th>Hourly</th>
                  <th>Daily</th>
                  <th>Monthly</th>
                </tr>
              </thead>
              <tbody>
                {standPricing.map((stand) => (
                  <tr key={stand.stand_id}>
                    <td>
                      <Text weight={500}>{stand.stand_number}</Text>
                    </td>
                    <td>{stand.stand_type.replace("_", " ")}</td>
                    <td>
                      <NumberInput
                        size="xs"
                        precision={2}
                        step={0.5}
                        value={getPriceWithEdit(stand, "hourly_rate_light")}
                        onChange={(value) =>
                          handleStandPriceChange(
                            stand.stand_id,
                            "hourly_rate_light",
                            value
                          )
                        }
                        styles={{
                          input: {
                            backgroundColor:
                              standPricingEdits[stand.stand_id]
                                ?.hourly_rate_light !== undefined
                                ? "#fff3cd"
                                : undefined,
                          },
                        }}
                      />
                    </td>
                    <td>
                      <NumberInput
                        size="xs"
                        precision={2}
                        step={5}
                        value={getPriceWithEdit(stand, "daily_rate_light")}
                        onChange={(value) =>
                          handleStandPriceChange(
                            stand.stand_id,
                            "daily_rate_light",
                            value
                          )
                        }
                        styles={{
                          input: {
                            backgroundColor:
                              standPricingEdits[stand.stand_id]
                                ?.daily_rate_light !== undefined
                                ? "#fff3cd"
                                : undefined,
                          },
                        }}
                      />
                    </td>
                    <td>
                      <NumberInput
                        size="xs"
                        precision={2}
                        step={10}
                        value={getPriceWithEdit(stand, "monthly_rate_light")}
                        onChange={(value) =>
                          handleStandPriceChange(
                            stand.stand_id,
                            "monthly_rate_light",
                            value
                          )
                        }
                        styles={{
                          input: {
                            backgroundColor:
                              standPricingEdits[stand.stand_id]
                                ?.monthly_rate_light !== undefined
                                ? "#fff3cd"
                                : undefined,
                          },
                        }}
                      />
                    </td>
                    <td>
                      <NumberInput
                        size="xs"
                        precision={2}
                        step={0.5}
                        value={getPriceWithEdit(stand, "hourly_rate_heavy")}
                        onChange={(value) =>
                          handleStandPriceChange(
                            stand.stand_id,
                            "hourly_rate_heavy",
                            value
                          )
                        }
                        styles={{
                          input: {
                            backgroundColor:
                              standPricingEdits[stand.stand_id]
                                ?.hourly_rate_heavy !== undefined
                                ? "#fff3cd"
                                : undefined,
                          },
                        }}
                      />
                    </td>
                    <td>
                      <NumberInput
                        size="xs"
                        precision={2}
                        step={5}
                        value={getPriceWithEdit(stand, "daily_rate_heavy")}
                        onChange={(value) =>
                          handleStandPriceChange(
                            stand.stand_id,
                            "daily_rate_heavy",
                            value
                          )
                        }
                        styles={{
                          input: {
                            backgroundColor:
                              standPricingEdits[stand.stand_id]
                                ?.daily_rate_heavy !== undefined
                                ? "#fff3cd"
                                : undefined,
                          },
                        }}
                      />
                    </td>
                    <td>
                      <NumberInput
                        size="xs"
                        precision={2}
                        step={10}
                        value={getPriceWithEdit(stand, "monthly_rate_heavy")}
                        onChange={(value) =>
                          handleStandPriceChange(
                            stand.stand_id,
                            "monthly_rate_heavy",
                            value
                          )
                        }
                        styles={{
                          input: {
                            backgroundColor:
                              standPricingEdits[stand.stand_id]
                                ?.monthly_rate_heavy !== undefined
                                ? "#fff3cd"
                                : undefined,
                          },
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};
